import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { authService } from '../../services/authService';

const TestReceipt = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [authError, setAuthError] = useState('');

  // Check admin status on mount
  useEffect(() => {
    const user = authService.getCurrentUser();
    if (!authService.isAuthenticated()) {
      setAuthError('You must be logged in to access this page.');
      setTimeout(() => navigate('/login'), 2000);
    } else if (!user?.is_admin) {
      setAuthError('Admin access required. Redirecting...');
      setTimeout(() => navigate('/admin/campaigns'), 2000);
    }
  }, [navigate]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(selectedFile.type)) {
        setError('Please select a valid image (JPG, PNG) or PDF file');
        return;
      }

      // Validate file size (10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setFile(selectedFile);
      setError('');
      setResult(null);

      // Create preview for images
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setPreview(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('receipt', file);

      const response = await api.post('/public/test-receipt', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setResult(response.data);
      } else {
        setError(response.data.error || 'Failed to extract text');
      }
    } catch (err) {
      console.error('Error:', err);
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to extract text from receipt. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError('');
    // Reset file input
    const fileInput = document.getElementById('receipt-file');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getStructuredText = () => {
    if (!result) return '';
    if (result.structuredReceipt) {
      return JSON.stringify(result.structuredReceipt, null, 2);
    }
    if (result.text) {
      return result.text;
    }
    if (result.metadata?.preview) {
      return result.metadata.preview;
    }
    return '';
  };

  const handleCopyText = () => {
    const textToCopy = getStructuredText();
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy);
      alert('Content copied to clipboard!');
    }
  };

  // Show auth error if not authenticated or not admin
  if (authError) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-slate-950 px-4 py-8 sm:px-6 lg:px-8">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-20 top-8 h-72 w-72 rounded-full bg-fuchsia-500/25 blur-3xl" />
          <div className="absolute right-0 top-32 h-80 w-80 rounded-full bg-cyan-400/20 blur-3xl" />
        </div>
        <div className="relative flex min-h-[80vh] items-center justify-center">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/95 p-7 shadow-2xl backdrop-blur">
            <div className="text-center">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              </div>
              <h2 className="mb-2 text-2xl font-bold text-slate-900">Access Denied</h2>
              <p className="text-slate-600">{authError}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 px-4 py-8 sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-16 top-8 h-72 w-72 rounded-full bg-indigo-500/30 blur-3xl" />
        <div className="absolute right-6 top-24 h-96 w-96 rounded-full bg-cyan-400/20 blur-3xl" />
        <div className="absolute bottom-0 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-violet-500/20 blur-3xl" />
      </div>
      <div className="relative mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 rounded-3xl border border-white/15 bg-gradient-to-br from-slate-900/80 via-slate-900/55 to-slate-800/45 p-6 text-white shadow-2xl backdrop-blur-xl sm:p-8">
          <p className="mb-3 inline-flex items-center rounded-full border border-cyan-300/30 bg-cyan-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-cyan-100">
            Admin Utility
          </p>
          <h1 className="mb-2 text-3xl font-bold tracking-tight sm:text-4xl">
            Receipt OCR Test Console
          </h1>
          <p className="max-w-3xl text-sm text-slate-200/90 sm:text-base">
            Upload a receipt image or PDF to extract structured content instantly. This tool processes files in-memory and does not persist uploads.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-5">
          {/* Upload Form */}
          <div className="lg:col-span-3">
            <div className="rounded-3xl border border-white/15 bg-white/95 p-6 shadow-2xl backdrop-blur sm:p-7">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* File Upload */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-800">
                    Upload Receipt
                  </label>
                  <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 shadow-sm">
                    <input
                      id="receipt-file"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,application/pdf"
                      onChange={handleFileChange}
                      disabled={loading}
                      className="block w-full cursor-pointer text-sm text-slate-500 file:mr-4 file:rounded-xl file:border-0 file:bg-slate-900 file:px-4 file:py-2.5 file:text-sm file:font-semibold file:text-white hover:file:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                    />
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">JPG / PNG / PDF</span>
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">Max 10MB</span>
                    </div>
                  </div>
                </div>

                {file && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4">
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">Selected file</p>
                    <p className="truncate text-sm font-medium text-slate-800">{file.name}</p>
                    <p className="mt-1 text-xs text-slate-600">
                      {formatFileSize(file.size)} • {file.type || 'Unknown type'}
                    </p>
                  </div>
                )}

                {/* Preview */}
                {preview && (
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-800">
                      Preview
                    </label>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-inner">
                      <img
                        src={preview}
                        alt="Receipt preview"
                        className="mx-auto max-h-72 w-auto rounded-xl border border-slate-200 shadow-sm"
                      />
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                {/* Buttons */}
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="submit"
                    disabled={!file || loading}
                    className="inline-flex w-full items-center justify-center rounded-xl border border-transparent bg-slate-900 px-6 py-3 text-base font-semibold text-white shadow transition hover:-translate-y-0.5 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <svg
                          className="-ml-1 mr-3 h-5 w-5 animate-spin text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      'Extract Text'
                    )}
                  </button>
                  {file && (
                    <button
                      type="button"
                      onClick={handleReset}
                      disabled={loading}
                      className="inline-flex w-full items-center justify-center rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Clear Selection
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>

          {/* Side panel + metadata */}
          <div className="lg:col-span-2">
            <div className="rounded-3xl border border-white/15 bg-white/95 p-6 shadow-2xl backdrop-blur sm:p-7">
              <h2 className="mb-4 text-lg font-semibold text-slate-900">Upload Guidelines</h2>
              <div className="space-y-3 text-sm text-slate-600">
                <p className="rounded-xl bg-slate-50 p-3">Use clear photos with good lighting and avoid cropped totals for best OCR quality.</p>
                <p className="rounded-xl bg-slate-50 p-3">PDF and image uploads are validated client-side before processing.</p>
                <p className="rounded-xl bg-slate-50 p-3">Results can include structured JSON and fallback text preview when available.</p>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-200 p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Formats</p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">JPG, PNG, PDF</p>
                </div>
                <div className="rounded-xl border border-slate-200 p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Max Size</p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">10 MB</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        {result && (
          <div className="mt-6 rounded-3xl border border-white/15 bg-white/95 p-6 shadow-2xl backdrop-blur sm:p-7">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="mb-1 inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                  Extraction complete
                </p>
                <h2 className="text-2xl font-bold text-slate-900">
                  {result.structuredReceipt ? 'Structured Receipt JSON' : 'Response Content'}
                </h2>
              </div>
              <button
                onClick={handleCopyText}
                className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Copy Text
              </button>
            </div>

            {result.metadata?.provider && (
              <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                Provider: <span className="font-semibold text-slate-900">{result.metadata.provider}</span>
                {result.metadata.totalTextLength ? ` • Text Length: ${result.metadata.totalTextLength}` : ''}
              </div>
            )}

            {/* Extracted Text */}
            <div className="max-h-96 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed text-slate-800">
                {getStructuredText() || 'No content returned'}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestReceipt;

